import * as React from 'react';
import { API_URL } from '../config/apiConfig';
import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// MUI komponendid
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'; // Trainings
import AssignmentIcon from '@mui/icons-material/Assignment';       // Records
import AddBoxIcon from '@mui/icons-material/AddBox';               // Register for Training
import PersonIcon from '@mui/icons-material/Person';               // My Profile
import StoreIcon from '@mui/icons-material/Store';                 // My Affiliate
import ClassIcon from '@mui/icons-material/Class';                 // Classes
import GroupIcon from '@mui/icons-material/Group';                 // Members
import EventNoteIcon from '@mui/icons-material/EventNote';         // Plans
import MailIcon from '@mui/icons-material/Mail';                   // Messages
import EventIcon from '@mui/icons-material/Event';                 // Trainer Schedule
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckIcon from '@mui/icons-material/Check';
// BottomNavigation
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';

// Import the required API functions
import { checkHomeAffiliate } from '../api/getClassesApi';
import { getAffiliateById } from '../api/affiliateApi';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,

    backdropFilter: 'blur(24px)',
    border: '1px solid',

    borderColor: (theme.vars || theme).palette.divider,
    backgroundColor: theme.vars
        ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
        : alpha(theme.palette.background.default, 0.4),
    boxShadow: (theme.vars || theme).shadows[1],
}));

export default function AppAppBar() {
    const { isLoggedIn, role, logout, isTrainer, token} = useContext(AuthContext);
    const [homeAffiliateLogo, setHomeAffiliateLogo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
    // API_URL imported from config
    const navigate = useNavigate();
    const location = useLocation();

    const pricingPlan = localStorage.getItem('pricingPlan');

    // Kontrollime, kas ekraani laius on väiksem kui "md"
    // et näidata kas ülemist menüüd (desktop) või alumist nav'i (mobiil).
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    // Function to determine navigation based on role
    const getNavigationPath = () => {
        if (role === 'regular') return '/training-diary';
        if (role === 'affiliate') return '/affiliate-owner';
        if (role === 'trainer') return '/trainer';
        if (role === 'checkin') return '/checkin';
        return '/';
    };


    // Role switching functions
    const handleRoleMenuOpen = (event) => {
        setRoleMenuAnchor(event.currentTarget);
    };

    const handleRoleMenuClose = () => {
        setRoleMenuAnchor(null);
    };

    const handleRoleSwitch = (newRole) => {
        // Checkin puhul saada 'affiliate' roll serverisse
        const serverRole = newRole === 'checkin' ? 'affiliate' : newRole;

        // Saada roll serverisse
        fetch(`${API_URL}/auth/role`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ role: serverRole })
        })
            .then(res => res.json())
            .then(data => {
                if (data.error) {
                    console.error('Role switching error:', data.error);
                    return;
                }

                // Kui edukalt salvestatud, salvesta localStorage'sse ja navigeeri
                localStorage.setItem('role', newRole);

                if (newRole === 'trainer') {
                    navigate('/trainer');
                } else if (newRole === 'regular') {
                    navigate('/training-diary');
                } else if (newRole === 'affiliate') {
                    navigate('/affiliate-owner');
                } else if (newRole === 'checkin') {
                    navigate('/checkin');
                }

                // Update role in context and reload
                window.location.reload();
            })
            .catch(err => {
                console.error('Role switching failed:', err);
            });

        handleRoleMenuClose();
    };

    // Get available roles for the user (you might want to fetch this from user data)
    const getAvailableRoles = () => {
        // This should come from user's permissions/roles
        // For now, returning both regular and trainer as example
        return ['regular', 'trainer'];
    };

    const availableRoles = getAvailableRoles();
    const showRoleSwitcher = isLoggedIn && isTrainer && availableRoles.length > 1;

    // Fetch home affiliate logo for regular users
    useEffect(() => {
        const fetchHomeAffiliateLogo = async () => {
            if (isLoggedIn && role === 'regular') {
                setLoading(true);
                try {
                    // Get user's home affiliate ID
                    const homeAffiliateData = await checkHomeAffiliate();
                    const homeAffiliateId = homeAffiliateData.homeAffiliate;

                    if (homeAffiliateId) {
                        // Get affiliate details including the logo
                        const affiliateData = await getAffiliateById(homeAffiliateId);

                        if (affiliateData && affiliateData.logo) {
                            setHomeAffiliateLogo(affiliateData.logo);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching home affiliate logo:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchHomeAffiliateLogo();
    }, [isLoggedIn, role]);

    // --------------------------------
    // ÜLEMINE NAV-BAR: Desktopi menüü
    // --------------------------------
    let leftLinks = [];
    let rightLinks = [];

    if (!isLoggedIn) {
        // Mitte sisse logitud kasutaja
        leftLinks = [
            { name: 'Home', to: '/' },
            { name: 'Pricing', to: '/pricing' },
        ];
        rightLinks = [
            { name: 'Log In', to: '/login' },
            { name: 'Join Us', to: '/register' },
        ];
    } else {
        // Sisse logitud
        if (role === 'regular') {
            // Regulaarne kasutaja
            leftLinks = [
                { name: 'Training log', to: '/trainings' },
                { name: 'Records', to: '/records' },
                { name: 'Register for Training', to: '/register-training' },
            ];
            rightLinks = [
                { name: 'My Profile', to: '/my-profile' },
            ];
        } else if (role === 'affiliate') {
            // Affiliate
            leftLinks = [
                { name: 'My Affiliate', to: '/my-affiliate' },
                { name: 'Classes', to: '/classes' },
                { name: 'Members', to: '/members' },
                { name: 'Plans', to: '/plans' },
                ...(pricingPlan === 'premium' ? [{ name: 'Messages', to: '/messages' }] : [])
            ];

        } else if (role === 'trainer') {
            leftLinks = [
                { name: 'Classes', to: '/classes' },
                { name: 'Members', to: '/members' },
                { name: 'Schedule', to: '/trainer-schedule' },
            ];
        } else if (role === 'checkin') {
            leftLinks = [
                { name: 'Check-in', to: '/checkin' },
            ];
        }
    }

    // --------------------------------
    // ALUMINE NAV-BAR: Mobiilivaate menüü
    // --------------------------------
    // Defineerime rollipõhised nupud koos ikoonidega.
    // value: unikaalne string, mida saame kasutada 'BottomNavigation' value-kontrolliks.
    // to: link, kuhu suunata
    // label, icon, etc.
    let bottomNavItems = [];
    if (isLoggedIn && role === 'regular') {
        bottomNavItems = [
            { value: '/trainings', label: 'Training log', icon: <FitnessCenterIcon /> },
            { value: '/records', label: 'Records', icon: <AssignmentIcon /> },
            { value: '/register-training', label: 'Register', icon: <AddBoxIcon /> },
            { value: '/my-profile', label: 'Profile', icon: <PersonIcon /> },
        ];
    } else if (isLoggedIn && role === 'affiliate') {
        bottomNavItems = [
            { value: '/my-affiliate', label: 'Affiliate', icon: <StoreIcon /> },
            { value: '/classes', label: 'Classes', icon: <ClassIcon /> },
            { value: '/members', label: 'Members', icon: <GroupIcon /> },
            { value: '/plans', label: 'Plans', icon: <EventNoteIcon /> },
            { value: '/messages', label: 'Messages', icon: <MailIcon /> },
        ];
    } else if (isLoggedIn && role === 'trainer') {
        bottomNavItems = [
            { value: '/classes', label: 'Classes', icon: <ClassIcon /> },
            { value: '/members', label: 'Members', icon: <GroupIcon /> },
            { value: '/trainer-schedule', label: 'Schedule', icon: <EventIcon /> },
        ];
    } else if (isLoggedIn && role === 'checkin') {
        bottomNavItems = [
            { value: '/checkin', label: 'Check-in', icon: <PersonIcon /> },
        ];
    }

    // Leiame, milline tab on aktiivne vastavalt praegusele URL-ile (location.pathname).
    // Kui 'location.pathname' on täpne, valime sama bottomNavItem.value.
    // Lihtsuse mõttes võib kasutada: item.value === location.pathname ?
    // Kui aga teed nested route'e vms, siis peab sobivuse loogikat täiendama.
    const currentValue = bottomNavItems.find(item => location.pathname.startsWith(item.value))?.value || '';

    const handleBottomNavChange = (event, newValue) => {
        // Suuname React Routeri abil
        navigate(newValue);
    };

    // --------------------------------
    // JSX struktuur
    // --------------------------------

    return (
        <>
            {/* Ülemine AppBar - sticky, et mobiilis scrollides jääks alati nähtavale */}
            <AppBar position="sticky" enableColorOnDark
                    sx={{
                        boxShadow: 0,
                        backgroundColor: 'transparent',
                        width: '100vw',
                        top: 0,
                    }}>

                <StyledToolbar variant="dense" disableGutters
                               sx={{ borderBottom: { xs: '2px solid #cccccc', md: 'none' } }}>

                    {/* NAVIGATION MOBILE VIEW */}
                    <Box sx={{
                        width: '100%',
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        {/* Keskel: Logo (either affiliate logo or default favicon) */}
                        <Box sx={{
                            flexGrow: 1,
                            display: 'flex',
                            ml: 1,
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            height: '50px',  // Suurem konteineri kõrgus
                            width: '140px'   // Fikseeritud laius
                        }}>
                            {isLoggedIn && role === 'regular' ? (
                                loading ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <img
                                        src={homeAffiliateLogo || "/favicon.png"}
                                        alt={homeAffiliateLogo ? "Affiliate Logo" : "App Logo"}
                                        style={{
                                            maxWidth: '100%',      // Võta kogu laius
                                            maxHeight: '100%',     // Võta kogu kõrgus
                                            width: 'auto',         // Laius automaatselt et säilitada proportsioone
                                            height: 'auto',        // Kõrgus automaatselt et säilitada proportsioone
                                            objectFit: 'contain',  // Mahuta kogu logo konteinerisse
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => navigate(getNavigationPath())}
                                    />
                                )
                            ) : (
                                <img
                                    src="/favicon.png"
                                    alt="App Logo"
                                    style={{
                                        maxHeight: '40px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => navigate(getNavigationPath())}
                                />
                            )}
                        </Box>

                        {role === null && (
                            <Box sx={{ display: { xs: 'flex', md: 'flex' }, gap: 1, ml: 2 }}>
                                {leftLinks.map((link) =>
                                    link.action ? (
                                        <Button
                                            key={link.name}
                                            onClick={link.action}
                                            variant="text"
                                            color="info"
                                            size="small"
                                        >
                                            {link.name}
                                        </Button>
                                    ) : (
                                        <Button
                                            key={link.name}
                                            component={Link}
                                            to={link.to}
                                            variant="text"
                                            color="info"
                                            size="small"
                                        >
                                            {link.name}
                                        </Button>
                                    )
                                )}
                            </Box>
                        )}

                        {/* Paremal: Role switcher, Help ja Logout/Login nupud */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* Role switcher - only for logged in users with multiple roles */}
                            {showRoleSwitcher && (
                                <>
                                    <IconButton
                                        color="primary"
                                        onClick={handleRoleMenuOpen}
                                        sx={{ mr: 0.5 }}
                                        title="Switch Role"
                                    >
                                        <SwapHorizIcon />
                                    </IconButton>
                                    <Menu
                                        anchorEl={roleMenuAnchor}
                                        open={Boolean(roleMenuAnchor)}
                                        onClose={handleRoleMenuClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                    >
                                        {availableRoles.map((roleOption) => (
                                            <MenuItem
                                                key={roleOption}
                                                onClick={() => handleRoleSwitch(roleOption)}
                                                selected={role === roleOption}
                                            >
                                                <ListItemIcon>
                                                    {role === roleOption ? <CheckIcon /> : null}
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {roleOption === 'regular' ? 'Regular User' :
                                                        roleOption === 'trainer' ? 'Trainer' :
                                                            roleOption === 'affiliate' ? 'Affiliate' :
                                                                roleOption === 'checkin' ? 'Check-in' : roleOption}
                                                </ListItemText>
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </>
                            )}

                            <HelpIcon
                                color="primary"
                                sx={{ mr: 0.5, ml: 0.5, cursor: 'pointer' }}
                                onClick={() => navigate('/help')}
                            />

                            {isLoggedIn ? (
                                <IconButton color="primary" onClick={() => logout(navigate)} sx={{ mr: 1 }}>
                                    <LogoutIcon />
                                </IconButton>
                            ) : (
                                <IconButton color="primary" onClick={() => navigate('/login')} sx={{ mr: 1 }}>
                                    <PersonIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    {/* DESKTOP VIEW */}
                    {/* Vasakul Logo */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 2, flexGrow: 1, alignItems: 'center', px: 0 }}>
                        <img
                            src="/logo2.png"
                            alt="Irontrack Logo"
                            style={{ height: '50px', cursor: 'pointer' }}
                            onClick={() => navigate(getNavigationPath())}
                        />
                    </Box>

                    {/* Desktop menüü (Left) */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, ml: 2 }}>
                        {leftLinks.map((link) =>
                            link.action ? (
                                <Button
                                    key={link.name}
                                    onClick={link.action}
                                    variant="text"
                                    color="info"
                                    size="small"
                                >
                                    {link.name}
                                </Button>
                            ) : (
                                <Button
                                    key={link.name}
                                    component={Link}
                                    to={link.to}
                                    variant="text"
                                    color="info"
                                    size="small"
                                >
                                    {link.name}
                                </Button>
                            )
                        )}
                    </Box>

                    {/* Desktop menüü (Right) - Role switcher, Help, Profile ja Log Out nupud */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, mr: 2, alignItems: 'center' }}>
                        {/* Role switcher - only for logged in users with multiple roles */}
                        {showRoleSwitcher && (
                            <>
                                <IconButton
                                    color="primary"
                                    onClick={handleRoleMenuOpen}
                                    title="Switch Role"
                                >
                                    <SwapHorizIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={roleMenuAnchor}
                                    open={Boolean(roleMenuAnchor)}
                                    onClose={handleRoleMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    {availableRoles.map((roleOption) => (
                                        <MenuItem
                                            key={roleOption}
                                            onClick={() => handleRoleSwitch(roleOption)}
                                            selected={role === roleOption}
                                        >
                                            <ListItemIcon>
                                                {role === roleOption ? <CheckIcon /> : null}
                                            </ListItemIcon>
                                            <ListItemText>
                                                {roleOption === 'regular' ? 'Regular User' :
                                                    roleOption === 'trainer' ? 'Trainer' :
                                                        roleOption === 'affiliate' ? 'Affiliate' :
                                                            roleOption === 'checkin' ? 'Check-in' : roleOption}
                                            </ListItemText>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}

                        <HelpIcon
                            color="primary"
                            sx={{ margin: 1, cursor: 'pointer' }}
                            onClick={() => navigate('/help')}
                        />

                        {rightLinks.map((link) =>
                            link.action ? (
                                <Button
                                    key={link.name}
                                    onClick={link.action}
                                    color="primary"
                                    variant="text"
                                    size="small"
                                >
                                    {link.name}
                                </Button>
                            ) : (
                                <Button
                                    key={link.name}
                                    component={Link}
                                    to={link.to}
                                    color="primary"
                                    variant="contained"
                                    size="small"
                                >
                                    {link.name}
                                </Button>
                            )
                        )}

                        {isLoggedIn && (
                            <IconButton
                                color="primary"
                                onClick={() => logout(navigate)}
                            >
                                <LogoutIcon />
                            </IconButton>
                        )}
                    </Box>
                </StyledToolbar>

            </AppBar>

            {/* Alumine nav (mobiilis), ainult siis kui kasutaja on sisse logitud.
          Kui eelistad näidata seda ka siis, kui kasutaja pole sisseloginud,
          võid tingimust muuta. */}
            {isLoggedIn && isMobile && bottomNavItems.length > 0 && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: 'divider',
                        zIndex: 1200, // Alla modalide (1300), et ClassModal ja kinnitusaknad avaneksid selle kohale
                    }}
                >
                    <BottomNavigation
                        showLabels
                        value={currentValue}
                        onChange={handleBottomNavChange}
                        sx={{
                            pb: 1,
                            height: "70px",
                            // Stilistika, nt kui aktiivsel itemil tahad teist värvi ikooni
                            // ja top borderit vms, saad vastavaid MUI-teemasid override'ida
                            // või kasutada sx-stiili.
                            '& .Mui-selected': {
                                color: 'primary.main',
                                borderTop: '3px solid',
                                borderColor: 'primary.main',
                            },
                            // Make icon and text larger for better usability
                            '& .MuiBottomNavigationAction-root': {
                                minHeight: '70px',
                                padding: '6px 0',
                            },
                            '& .MuiSvgIcon-root': {
                                fontSize: '26px',
                            },
                            '& .MuiBottomNavigationAction-label': {
                                fontSize: '0.8rem',
                                marginTop: '4px',
                            }
                        }}
                    >
                        {bottomNavItems.map((item) => (
                            <BottomNavigationAction
                                key={item.value}
                                label={item.label}
                                icon={item.icon}
                                value={item.value}
                            />
                        ))}
                    </BottomNavigation>
                </Box>
            )}
        </>
    );
}